// #region Imports
import { useRef } from 'react';
import { Dialog, TextArea } from '@radix-ui/themes';
import { useFormContext } from 'react-hook-form';
import { FaArrowLeft, FaPaperclip, FaTimes } from 'react-icons/fa';
import { AZButton } from '../AZButton';
import { useAttachments } from './useAttachments';
import {
  DEFAULT_ALLOWED_MIME_TYPES,
  DEFAULT_MAX_FILES,
  DEFAULT_MAX_TOTAL_SIZE,
  formatBytes,
  type AttachmentConstraints,
} from '../../utils/fileUtils';
import './AZFeedback.css';
// #endregion

// #region Types
interface BugScreenProps {
  isManual: boolean;
  isLoading: boolean;
  attachmentOptions?: AttachmentConstraints;
  onGoBack: () => void;
  onClose: () => void;
}
// #endregion

// #region Component
const BugScreen = ({ isManual, isLoading, attachmentOptions, onGoBack, onClose }: BugScreenProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const { files, errors: attachmentErrors, addFiles, removeFile } = useAttachments(attachmentOptions);
  const inputRef = useRef<HTMLInputElement>(null);

  const acceptedTypes = (attachmentOptions?.allowedMimeTypes ?? DEFAULT_ALLOWED_MIME_TYPES).join(',');
  const maxFiles = attachmentOptions?.maxFiles ?? DEFAULT_MAX_FILES;
  const maxTotalSize = attachmentOptions?.maxTotalSize ?? DEFAULT_MAX_TOTAL_SIZE;

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) void addFiles(event.target.files);
    // Reset so selecting the same file again still fires onChange.
    event.target.value = '';
  };

  const submitError = errors['attachments'];
  const submitRootError = errors.root?.submit;

  return (
    <>
      {/* #region Header */}
      <div className="az-feedback-header">
        <div className="az-feedback-title-group">
          <Dialog.Title className="az-feedback-title">Bug Report</Dialog.Title>
          <p className="az-feedback-disclaimer">Describe the issue and steps to reproduce it</p>
        </div>
        <div className="az-feedback-header-buttons">
          {isManual && (
            <AZButton
              variant="ghost"
              description="Go back to menu"
              type="button"
              className="az-feedback-icon-button"
              onClick={onGoBack}
              aria-label="Go back"
            >
              <FaArrowLeft aria-hidden="true" />
            </AZButton>
          )}
          <AZButton
            type="button"
            variant="ghost"
            description="Close"
            className="az-feedback-icon-button"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes aria-hidden="true" />
          </AZButton>
        </div>
      </div>
      {/* #endregion */}

      {/* #region Form */}
      <div className="az-feedback-form">
        <div>
          <label className="az-feedback-label" htmlFor="bugComment">
            Description *
          </label>
          <TextArea
            className="az-feedback-textarea"
            id="bugComment"
            placeholder="Describe what happened and how to reproduce the issue…"
            {...register('bugComment', { required: 'Description is required' })}
          />
          {errors['bugComment'] && (
            <p className="az-feedback-error-msg" role="alert">
              {String(errors['bugComment']?.message ?? 'Required')}
            </p>
          )}
        </div>

        {/* #region Attachments */}
        <div className="az-feedback-attachments">
          <label className="az-feedback-label" htmlFor="bugAttachments">
            Attachments (optional)
          </label>
          <p className="az-feedback-disclaimer">
            Images · up to {maxFiles} files, {formatBytes(maxTotalSize)} total
          </p>

          <input
            ref={inputRef}
            id="bugAttachments"
            className="az-feedback-attachment-input"
            type="file"
            multiple
            accept={acceptedTypes}
            onChange={handleSelect}
          />
          <AZButton
            type="button"
            variant="secondary"
            size="sm"
            icon={<FaPaperclip aria-hidden="true" />}
            onClick={() => inputRef.current?.click()}
          >
            Add attachments
          </AZButton>

          {files.length > 0 && (
            <ul className="az-feedback-attachment-list">
              {files.map((file, index) => (
                <li key={`${file.name}-${index}`} className="az-feedback-attachment-item">
                  <span className="az-feedback-attachment-meta">
                    {file.name} <span className="az-feedback-attachment-size">({formatBytes(file.size)})</span>
                  </span>
                  <AZButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    description="Remove attachment"
                    className="az-feedback-icon-button"
                    onClick={() => removeFile(index)}
                    aria-label={`Remove ${file.name}`}
                  >
                    <FaTimes aria-hidden="true" />
                  </AZButton>
                </li>
              ))}
            </ul>
          )}

          {attachmentErrors.map((error, index) => (
            <p key={index} className="az-feedback-error-msg" role="alert">
              {error.message}
            </p>
          ))}
          {submitError && (
            <p className="az-feedback-error-msg" role="alert">
              {String(submitError.message ?? 'Invalid attachments')}
            </p>
          )}
        </div>
        {/* #endregion */}
      </div>
      {/* #endregion */}

      {/* #region Footer */}
      <div className="az-feedback-footer">
        {submitRootError && (
          <p className="az-feedback-error-msg" role="alert">
            {String(submitRootError.message ?? 'Failed to send. Please try again.')}
          </p>
        )}
        <AZButton
          type="submit"
          variant="primary"
          disabled={isLoading || attachmentErrors.length > 0}
          isLoading={isLoading}
        >
          Submit
        </AZButton>
      </div>
      {/* #endregion */}
    </>
  );
};
// #endregion

// #region Display Name
BugScreen.displayName = 'BugScreen';
// #endregion

export { BugScreen };
